'use client'

import Image from "next/image"
import { useEffect, useMemo, useRef, useState } from "react"
import type { NotificationConfig } from "@/app/stores/popup-notification-store"

const NOTIFICATION_DURATION = 4000
const FALLBACK_IMAGE = "/assets/health/companies/square/reassurez-moi.png"

interface PopupNotificationProps {
    config: NotificationConfig | null
    onClose: () => void
}

const cleanInsuranceName = (name: string): string =>
    name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/ /g, "_")
        .toLowerCase()

const resolveImagePath = (
    image: string | undefined,
    useLandingImage: boolean | undefined
): string => {
    if (useLandingImage) return "/assets/health/landing/SQUARE_DEFAULT_SALE.png"
    if (!image) return FALLBACK_IMAGE
    // Always normalize: the API returns names like "PREPAR-IARD" and the file
    // names on disk are lowercase + accent-stripped + spaces→underscores.
    const stripped = image.replace(/\.png$/i, "")
    return `/assets/health/companies/square/${cleanInsuranceName(stripped)}.png`
}

export default function PopupNotification({ config, onClose }: PopupNotificationProps) {
    const [isVisible, setIsVisible] = useState(false)
    const [timerWidth, setTimerWidth] = useState(100)
    const [imgErrored, setImgErrored] = useState(false)
    const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // le wording nominatif Mercer ne vaut que pour la 1re demande de rappel, ensuite c'est le format commun
    const conseiller = config?.followUp ? undefined : config?.conseiller
    // sans label il n'y a personne à nommer : c'est le seul cas qui retombe sur la ligne générique
    const announcedPartner = config?.partner?.label ? config.partner : null

    const stopTimer = () => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current)
            timerIntervalRef.current = null
        }
    }

    useEffect(() => {
        if (!config) return

        setTimerWidth(100)
        setImgErrored(false)

        const showTimer = setTimeout(() => {
            setIsVisible(true)
            const intervalTime = 100
            const step = (intervalTime / NOTIFICATION_DURATION) * 100
            timerIntervalRef.current = setInterval(() => {
                setTimerWidth((prev) => Math.max(prev - step, 0))
            }, intervalTime)
        }, 100)

        const hideTimer = setTimeout(() => {
            setIsVisible(false)
            stopTimer()
            closeTimerRef.current = setTimeout(() => onClose(), 500)
        }, NOTIFICATION_DURATION + 100)

        return () => {
            clearTimeout(showTimer)
            clearTimeout(hideTimer)
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
            stopTimer()
        }
    }, [config, onClose])

    useEffect(() => () => stopTimer(), [])

    const computedSrc = useMemo(
        () => (config ? resolveImagePath(config.image, config.useLandingImage) : FALLBACK_IMAGE),
        [config?.image, config?.useLandingImage]
    )

    if (!config) return null

    const imageSrc = imgErrored ? FALLBACK_IMAGE : computedSrc

    return (
        <div
            data-test="popup-notification"
            className={`
                fixed z-[1000] bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.15)]
                transition-transform duration-200 ease-out
                bottom-5 right-5 min-w-[360px] min-h-24
                max-md:bottom-0 max-md:right-0 max-md:left-0 max-md:w-full max-md:rounded-t max-md:rounded-b-none
                ${isVisible ? "translate-x-0" : "translate-x-[400px]"}
            `}
        >
            {/* Timer Bar */}
            <div className="w-full h-0.5 bg-green-200 rounded-t-lg overflow-hidden">
                <div
                    className="h-full bg-green-800 transition-all duration-100 linear rounded-tl-lg"
                    style={{ width: `${timerWidth}%` }}
                />
            </div>

            {/* Content */}
            <div className="flex items-center p-4 gap-3">
                <Image
                    src={imageSrc}
                    alt={config.text}
                    width={48}
                    height={48}
                    className="flex-shrink-0 object-contain rounded"
                    onError={() => setImgErrored(true)}
                />
                <p className="m-0 text-sm font-medium leading-normal">
                    <span className="font-bold">
                        Votre demande de {config.text} a bien été reçue.
                    </span>
                    <br />
                    {conseiller ? (
                        <>
                            {/* l'espace reste dans le nœud de texte : un <br> n'en ajoute aucun dans textContent */}
                            Votre conseiller {conseiller.name} vous rappellera{" "}
                            <br/>
                            avec le numéro suivant{" "}
                            <span className="text-green-500 font-semibold">{conseiller.phone}</span>
                        </>
                    ) : announcedPartner ? (
                        <>
                            {config.followUp && (
                                <>
                                    {"En cas de refus de notre premier partenaire, "}
                                    <br />
                                </>
                            )}
                            {announcedPartner.label}
                            {announcedPartner.orias && ` (N° ORIAS : ${announcedPartner.orias})`}
                            {" va "}
                            {/* la ligne dépasse la largeur de la popup une fois le numéro ajouté ;
                                l'espace reste dans le nœud de texte, un <br> n'en ajoute aucun dans textContent */}
                            <br />
                            {"vous recontacter"}
                            {announcedPartner.phone && (
                                <>
                                    {" avec le numéro suivant : "}
                                    <span className="text-green-500 font-semibold">{announcedPartner.phone}</span>
                                </>
                            )}
                        </>
                    ) : (
                        <>Un expert va vous recontacter au plus vite 👋</>
                    )}
                </p>
            </div>
        </div>
    )
}
